import React, { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Search, UserCog, KeyRound, ToggleLeft, ToggleRight, Trash2, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const BUILTIN_ROLES = ["admin", "operations", "kitchen", "driver", "setup", "customer"];
const BUILTIN_COLORS = {
  admin: "#D4AF37", operations: "#3B82F6", kitchen: "#F59E0B",
  driver: "#10B981", setup: "#A78BFA", customer: "#A1A1AA",
};

// Backwards-compat aliases (existing components below reference these).
const ALL_ROLES_FALLBACK = BUILTIN_ROLES;
const ROLE_COLORS = BUILTIN_COLORS;

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [customRoles, setCustomRoles] = useState([]);

  const allRoleKeys = [...BUILTIN_ROLES, ...customRoles.map((r) => r.key)];
  const roleColors = { ...BUILTIN_COLORS, ...Object.fromEntries(customRoles.map((r) => [r.key, r.color])) };
  const roleLabels = {
    ...Object.fromEntries(BUILTIN_ROLES.map((r) => [r, r])),
    ...Object.fromEntries(customRoles.map((r) => [r.key, r.name_en])),
  };

  const load = async () => {
    const r = await api.get(`/users${roleFilter ? `?role=${roleFilter}` : ""}`);
    setUsers(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [roleFilter]);
  useEffect(() => {
    api.get("/custom-roles").then((r) => setCustomRoles(r.data || [])).catch(() => {});
  }, []);

  const filtered = users.filter((u) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s);
  });

  const toggleActive = async (u) => {
    if (u.id === me.id) return toast.error("Cannot deactivate yourself");
    await api.patch(`/users/${u.id}`, { active: !(u.active !== false) });
    toast.success(`User ${u.active === false ? "activated" : "deactivated"}`);
    load();
  };
  const del = async (u) => {
    if (u.id === me.id) return toast.error("Cannot delete yourself");
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    await api.delete(`/users/${u.id}`);
    toast.success("User deleted");
    load();
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="users-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Access Control</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Users & Staff</h1>
          <p className="text-zinc-500 text-sm mt-2">Create accounts, assign roles, reset passwords and deactivate access.</p>
        </div>
        <Button onClick={() => setShowAdd(true)} data-testid="add-user" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
          <Plus className="w-4 h-4 mr-1.5" /> Add User
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input placeholder="Search by name or email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="users-search"
            className="pl-9 bg-[#0a0a0a] border-[#2a2a2a] h-11 rounded-sm" />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="role-filter"
          className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-sm px-3 h-11 text-sm capitalize">
          <option value="">All roles</option>
          {allRoleKeys.map((r) => <option key={r} value={r}>{roleLabels[r] || r}</option>)}
        </select>
      </div>

      <div className="card-lux overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b border-[#1a1a1a]">
            <th className="p-4 label-eyebrow text-[10px]">User</th>
            <th className="p-4 label-eyebrow text-[10px]">Roles</th>
            <th className="p-4 label-eyebrow text-[10px]">Contact</th>
            <th className="p-4 label-eyebrow text-[10px]">Status</th>
            <th className="p-4 label-eyebrow text-[10px]">Created</th>
            <th className="p-4 label-eyebrow text-[10px] text-right">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map((u) => {
              const roles = Array.isArray(u.roles) && u.roles.length ? u.roles : [u.role];
              const active = u.active !== false;
              return (
                <tr key={u.id} className="border-b border-[#1a1a1a] hover:bg-[#0c0c0c]" data-testid={`user-row-${u.id}`}>
                  <td className="p-4">
                    <div className="font-medium text-zinc-100">{u.name}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{u.email}</div>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1" data-testid={`roles-${u.id}`}>
                      {roles.map((r) => {
                        const color = roleColors[r] || "#A1A1AA";
                        const isPrimary = r === u.role;
                        const label = roleLabels[r] || r;
                        const isBuiltin = BUILTIN_ROLES.includes(r);
                        return (
                          <span key={r} className={`stage-chip text-[10px] ${isBuiltin ? "capitalize" : ""}`} style={{ color, borderColor: `${color}55`, background: `${color}10` }} title={isPrimary ? "Primary role" : "Additional role"}>
                            {label}{isPrimary && roles.length > 1 ? " ★" : ""}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="p-4 text-xs text-zinc-400">{u.phone || "—"}</td>
                  <td className="p-4">
                    <span className={`text-xs ${active ? "text-emerald-400" : "text-red-400"}`}>{active ? "Active" : "Inactive"}</span>
                  </td>
                  <td className="p-4 text-xs text-zinc-500">{u.created_at ? fmtDate(u.created_at) : "—"}</td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditUser(u)} title="Edit role" className="p-2 hover:text-[#D4AF37]" data-testid={`edit-${u.id}`}><UserCog className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setPwUser(u)} title="Reset password" className="p-2 hover:text-[#D4AF37]" data-testid={`pw-${u.id}`}><KeyRound className="w-3.5 h-3.5" /></button>
                      <button onClick={() => toggleActive(u)} title={active ? "Deactivate" : "Activate"} className="p-2 hover:text-[#D4AF37]" data-testid={`toggle-${u.id}`}>
                        {active ? <ToggleRight className="w-4 h-4 text-emerald-500" /> : <ToggleLeft className="w-4 h-4 text-zinc-500" />}
                      </button>
                      <button onClick={() => del(u)} title="Delete" className="p-2 hover:text-red-400" data-testid={`del-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AddUserDialog open={showAdd} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); toast.success("User created"); }} allRoles={allRoleKeys} roleColors={roleColors} roleLabels={roleLabels} />
      <EditUserDialog user={editUser} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); load(); toast.success("User updated"); }} allRoles={allRoleKeys} roleColors={roleColors} roleLabels={roleLabels} />
      <ResetPasswordDialog user={pwUser} onClose={() => setPwUser(null)} onSaved={() => { setPwUser(null); toast.success("Password reset"); }} />
    </div>
  );
}

function RoleSelector({ primary, roles, onChange, allRoles = ALL_ROLES_FALLBACK, roleColors = ROLE_COLORS, roleLabels = {} }) {
  // Internally, `additional` is the set of non-primary roles the admin explicitly toggled on.
  // `roles` is always derived as [primary, ...additional].
  const additional = roles.filter((r) => r !== primary);
  const toggle = (r) => {
    if (r === primary) return; // can't toggle primary off
    const next = additional.includes(r)
      ? additional.filter((x) => x !== r)
      : [...additional, r];
    onChange({ primary, roles: [primary, ...next] });
  };
  const setPrimary = (r) => {
    const nextAdditional = additional.filter((x) => x !== r);
    onChange({ primary: r, roles: [r, ...nextAdditional] });
  };
  return (
    <div className="space-y-2">
      <Label className="label-eyebrow text-[10px]">Primary Role</Label>
      <select value={primary} onChange={(e) => setPrimary(e.target.value)} data-testid="role-primary" className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm capitalize">
        {allRoles.map((r) => <option key={r} value={r}>{roleLabels[r] || r}</option>)}
      </select>
      <Label className="label-eyebrow text-[10px] block pt-2">Additional Roles (multi-select)</Label>
      <div className="grid grid-cols-2 gap-2">
        {allRoles.map((r) => {
          const checked = r === primary || additional.includes(r);
          const isPrimary = r === primary;
          const color = roleColors[r] || "#A1A1AA";
          const label = roleLabels[r] || r;
          const isBuiltin = ["admin", "operations", "kitchen", "driver", "setup", "customer"].includes(r);
          return (
            <label key={r} className={`flex items-center gap-2 px-3 py-2 rounded-sm border cursor-pointer transition-colors ${checked ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-[#2a2a2a] hover:border-[#3a3a3a]"} ${isPrimary ? "opacity-90" : ""}`}>
              <input
                type="checkbox"
                checked={checked}
                disabled={isPrimary}
                onChange={() => toggle(r)}
                data-testid={`role-cb-${r}`}
                className="accent-[#D4AF37]"
              />
              <span className={`text-sm ${isBuiltin ? "capitalize" : ""}`} style={{ color }}>{label}</span>
              {isPrimary && <span className="text-[9px] text-zinc-500 ml-auto">primary</span>}
            </label>
          );
        })}
      </div>
      <p className="text-[10px] text-zinc-500">Primary role is the user's main identity (used for default redirects and labels). Additional roles grant extra permissions.</p>
    </div>
  );
}

function AddUserDialog({ open, onClose, onSaved, allRoles, roleColors, roleLabels }) {
  const [f, setF] = useState({ email: "", password: "", name: "", role: "operations", roles: ["operations"], phone: "", active: true });
  useEffect(() => {
    if (open) setF({ email: "", password: "", name: "", role: "operations", roles: ["operations"], phone: "", active: true });
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Add User</DialogTitle><DialogDescription className="text-zinc-500 text-xs">Create an account; the user can sign in immediately. Assign one or more roles.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="add-name" className="bg-[#050505] border-[#2a2a2a]" />
          <Input type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="add-email" className="bg-[#050505] border-[#2a2a2a]" />
          <Input type="password" placeholder="Initial password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="add-password" className="bg-[#050505] border-[#2a2a2a]" />
          <Input placeholder="Phone (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="bg-[#050505] border-[#2a2a2a]" />
          <RoleSelector primary={f.role} roles={f.roles} onChange={({ primary, roles }) => setF({ ...f, role: primary, roles })} allRoles={allRoles} roleColors={roleColors} roleLabels={roleLabels} />
          <Button onClick={async () => { try { await api.post("/users", f); onSaved?.(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } }} data-testid="add-save"
            disabled={!f.email || !f.password || !f.name} className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C86B]">
            Create User
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, onClose, onSaved, allRoles, roleColors, roleLabels }) {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (user) {
      const rs = Array.isArray(user.roles) && user.roles.length ? user.roles : [user.role];
      setF({ name: user.name, role: user.role, roles: rs, phone: user.phone || "" });
    }
  }, [user]);
  if (!user || !f) return null;
  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">{user.name}</DialogTitle><DialogDescription className="text-zinc-500 text-xs">Update name, phone, primary role and additional roles. Multi-role: e.g., Kitchen + Driver.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-[#050505] border-[#2a2a2a]" />
          <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone" className="bg-[#050505] border-[#2a2a2a]" />
          <RoleSelector primary={f.role} roles={f.roles} onChange={({ primary, roles }) => setF({ ...f, role: primary, roles })} allRoles={allRoles} roleColors={roleColors} roleLabels={roleLabels} />
          <Button onClick={async () => { try { await api.patch(`/users/${user.id}`, f); onSaved?.(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } }} data-testid="edit-save" className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C86B]">Save Changes</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  if (!user) return null;
  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white">
        <DialogHeader><DialogTitle className="font-display text-2xl">Reset Password</DialogTitle><DialogDescription className="text-zinc-500 text-xs">Set a new password; the user will need to use it next login.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-zinc-400">Setting a new password for <span className="text-[#D4AF37]">{user.email}</span></div>
          <Input type="password" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="rp-input" className="bg-[#050505] border-[#2a2a2a]" />
          <Button disabled={pw.length < 4} onClick={async () => { await api.post(`/users/${user.id}/reset-password`, { new_password: pw }); onSaved?.(); }} data-testid="rp-save" className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C86B]">Reset Password</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
